import { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../Redux/hooks';
import { upsert } from '../../../Redux/studentsSlice';
import { canUse } from '../../../Redux/sessionSlice';
import { useTranslation } from '../../../i18n/useTranslation';
import { contactDisplayName, contactDisplayRelation } from '../../../Utils/DisplayNameUtil';
import type { StudentContactModel, StudentModel } from '../../../Models/StudentModel';
import { PremiumFeature } from '../../../Models/enums';
import { StudentService } from '../../../Services/StudentService';

/**
 * The people around a student: a parent, a second parent, whoever pays.
 *
 * `receivesPayments` is the one that matters beyond the card. It decides who
 * a payment reminder is addressed to, and more than one contact can carry it,
 * which is why it is a checkbox on each rather than a single choice.
 */
export function ContactEditor({ student }: { student: StudentModel }) {
  const { tr, language } = useTranslation();
  const dispatch = useAppDispatch();
  const unlocked = useAppSelector(state => canUse(state.session, PremiumFeature.PaymentReminders));
  const [name, setName] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [relation, setRelation] = useState('');
  const [relationEn, setRelationEn] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);

  function replaceContacts(contacts: StudentContactModel[]) {
    dispatch(upsert({ ...student, contacts }));
  }

  async function add() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const contact = await StudentService.addContact(student.name, {
        name: name.trim(),
        ...(nameEn.trim() ? { nameEn: nameEn.trim() } : {}),
        ...(relation.trim() ? { relation: relation.trim() } : {}),
        ...(relationEn.trim() ? { relationEn: relationEn.trim() } : {}),
        ...(phone.trim() ? { phone: phone.trim() } : {})
      });
      replaceContacts([...student.contacts, contact]);
      setName('');
      setNameEn('');
      setRelation('');
      setRelationEn('');
      setPhone('');
    } catch {
      // Entitlement is enforced on the server. Nothing was added.
    } finally {
      setBusy(false);
    }
  }

  async function togglePayer(contact: StudentContactModel, receives: boolean) {
    setBusy(true);
    try {
      const updated = await StudentService.updateContact(contact.id, { receivesPayments: receives ? 1 : 0 });
      replaceContacts(student.contacts.map(item => (item.id === contact.id ? updated : item)));
    } catch {
      // The checkbox falls back to the last value the server confirmed.
    } finally {
      setBusy(false);
    }
  }

  /** Saves one English spelling when its box loses focus, and only if it changed. */
  async function saveSpelling(contact: StudentContactModel, field: 'nameEn' | 'relationEn', value: string) {
    const next = value.trim() || null;
    if (next === (contact[field] ?? null)) return;
    try {
      const updated = await StudentService.updateContact(contact.id, { [field]: next });
      replaceContacts(student.contacts.map(item => (item.id === contact.id ? updated : item)));
    } catch {
      // The box keeps what was typed; the card keeps the last confirmed value.
    }
  }

  async function remove(id: number) {
    setBusy(true);
    try {
      await StudentService.removeContact(id);
      replaceContacts(student.contacts.filter(contact => contact.id !== id));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="editor-section is-wide">
      <h3>{tr('contactsTitle')}</h3>

      {/* Shown rather than hidden: a tutor should be able to see what the
          entitlement buys before deciding whether they want it. */}
      {!unlocked && <p className="editor-locked">{tr('contactsLocked')}</p>}

      {student.contacts.length > 0 && (
        <ul className="editor-contact-list">
          {student.contacts.map(contact => (
            <li key={contact.id}>
              <span className="editor-contact-identity">
                <strong>{contactDisplayName(contact, language)}</strong>
                {contact.relation && <small>{contactDisplayRelation(contact, language)}</small>}
                {contact.phone && <bdi dir="ltr">{contact.phone}</bdi>}
              </span>
              <label className="check-label">
                <input
                  type="checkbox"
                  checked={contact.receivesPayments === 1}
                  disabled={busy || !unlocked}
                  onChange={event => void togglePayer(contact, event.target.checked)}
                />
                <span>{tr('receivesPayments')}</span>
              </label>
              <button type="button" onClick={() => void remove(contact.id)} disabled={busy} aria-label={tr('remove')}>×</button>
              <span className="editor-contact-spelling">
                <input
                  className="text-input"
                  dir="ltr"
                  defaultValue={contact.nameEn ?? ''}
                  placeholder={tr('contactNameEn')}
                  aria-label={tr('contactNameEn')}
                  disabled={!unlocked}
                  onBlur={event => void saveSpelling(contact, 'nameEn', event.target.value)}
                />
                <input
                  className="text-input"
                  dir="ltr"
                  defaultValue={contact.relationEn ?? ''}
                  placeholder={tr('relationEn')}
                  aria-label={tr('relationEn')}
                  disabled={!unlocked}
                  onBlur={event => void saveSpelling(contact, 'relationEn', event.target.value)}
                />
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="editor-row">
        <label className="field">
          <span>{tr('contactName')}</span>
          <input className="text-input" value={name} onChange={e => setName(e.target.value)} disabled={!unlocked} />
        </label>
        <label className="field">
          <span>{tr('contactNameEn')}</span>
          <input className="text-input" dir="ltr" value={nameEn} onChange={e => setNameEn(e.target.value)} disabled={!unlocked} />
        </label>
        <label className="field">
          <span>{tr('relation')}</span>
          <input className="text-input" value={relation} onChange={e => setRelation(e.target.value)} disabled={!unlocked} />
        </label>
        <label className="field">
          <span>{tr('relationEn')}</span>
          <input className="text-input" dir="ltr" value={relationEn} onChange={e => setRelationEn(e.target.value)} disabled={!unlocked} />
        </label>
        <label className="field">
          <span>{tr('phone')}</span>
          <input className="text-input" type="tel" value={phone} onChange={e => setPhone(e.target.value)} disabled={!unlocked} />
        </label>
        <button type="button" className="ghost-button" onClick={() => void add()} disabled={busy || !unlocked || !name.trim()}>
          {tr('add')}
        </button>
      </div>
    </section>
  );
}
